import React, { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Copy, Send } from "lucide-react";

export const InfoRequestDialog = ({ dealer, open, onClose, onSent }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!open) return;
    setResult(null); setEmail(dealer.contact_email || ""); setName(dealer.contact_name || dealer.dealer_principal || "");
    api.get(`/dealers/${dealer.id}/info-requests`).then(({ data }) => setHistory(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dealer?.id]);

  const send = async (sendEmail) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dealers/${dealer.id}/info-request`, { recipient_email: email, recipient_name: name || null, message: msg || null, send_email: sendEmail });
      setResult(data); toast.success(sendEmail ? (data.emailed ? "Request emailed" : "Link created (email not delivered)") : "Link created"); onSent?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create request"); }
    finally { setBusy(false); }
  };
  const copy = (l) => { navigator.clipboard?.writeText(l); toast.success("Link copied"); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="info-request-dialog">
        <DialogHeader><DialogTitle>Request missing info from dealer</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="info-request-result">
            <div className="text-sm text-[#0B132B]">Secure link ready{result.emailed ? " and emailed." : "."} Asks for: <b>{result.requested.join(", ")}</b></div>
            <div className="flex gap-2"><Input readOnly value={result.link} className="h-9 rounded-sm text-xs" data-testid="info-request-link" /><Button variant="outline" size="sm" className="h-9 rounded-sm" onClick={() => copy(result.link)} data-testid="info-request-copy"><Copy size={13} /></Button></div>
            <div className="text-[11px] text-[#6C757D]">Expires {new Date(result.expires_at).toLocaleDateString()}</div>
            <Button onClick={onClose} className="rounded-sm" data-testid="info-request-done">Done</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-[#6C757D]">The dealer gets a secure, expiring link to fill in only what's missing (EIN, ACH, F&I contacts). You're notified when they submit.</p>
            <div><Label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Recipient email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} className="h-9 rounded-sm mt-1" data-testid="info-request-email" /></div>
            <div><Label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Recipient name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-sm mt-1" data-testid="info-request-name" /></div>
            <div><Label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Personal note (optional)</Label><Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} className="rounded-sm mt-1" data-testid="info-request-message" /></div>
            <div className="flex gap-2">
              <Button onClick={() => send(true)} disabled={busy || !email} className="rounded-sm bg-[#0B132B] hover:bg-[#1C2541]" data-testid="info-request-send"><Send size={13} className="mr-1" /> Email link</Button>
              <Button onClick={() => send(false)} disabled={busy || !email} variant="outline" className="rounded-sm" data-testid="info-request-link-only">Create link only</Button>
            </div>
            {history.length > 0 && <div className="border-t border-[#E9ECEF] pt-3">
              <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D] mb-1">Previous requests</div>
              <ul className="space-y-1 text-xs" data-testid="info-request-history">{history.map((h, i) => <li key={i} className="flex items-center justify-between gap-2"><span>{h.recipient_email} · <span className={h.status === "submitted" ? "text-emerald-700 font-semibold" : "text-[#6C757D]"}>{h.status}</span> · {new Date(h.created_at).toLocaleDateString()}</span>{h.status !== "submitted" && <button className="text-[#0B132B] underline" onClick={() => copy(h.link)}>copy link</button>}</li>)}</ul>
            </div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
